export function isLeaseDeal(dealType) {
  return String(dealType || "").trim().toUpperCase() === "LEASE";
}

function classificationKey(classification) {
  return String(classification || "").trim().toUpperCase();
}

/**
 * Default listing / co-operating commission % for Lease deals.
 * - Listing side (and double ender): 50 / 50
 * - Co-operating side: blank listing, 50 co-operating
 * Returns null when deal type is not Lease.
 */
export function leaseCommissionDefaults(dealType, classification) {
  if (!isLeaseDeal(dealType)) return null;
  const cls = classificationKey(classification);
  if (cls.includes("CO-OPERATING") || cls.includes("COOPERATING")) {
    return { listCommission: "", sellCommission: "50" };
  }
  return { listCommission: "50", sellCommission: "50" };
}

export function applyLeaseCommissionDefaults(keyInfo, { overwrite = true } = {}) {
  if (!keyInfo) return keyInfo;
  const defaults = leaseCommissionDefaults(
    keyInfo.dealType,
    keyInfo.classification
  );
  if (!defaults) return keyInfo;
  const next = { ...keyInfo };
  for (const [field, value] of Object.entries(defaults)) {
    const current = next[field];
    const empty = current === "" || current == null;
    if (overwrite || empty) {
      next[field] = value;
    }
  }
  return next;
}

/** Apply lease defaults onto a key-info-like object; no-op if not Lease. */
export function withLeaseCommissionDefaults(data) {
  return applyLeaseCommissionDefaults(data, { overwrite: true });
}

export function withLeaseCommissionChange(prev, name, value) {
  const updated = { ...prev, [name]: value };
  if (name === "dealType" || name === "classification") {
    return applyLeaseCommissionDefaults(updated, { overwrite: true });
  }
  return updated;
}

/** Merge PDF trade fields into key info.
 * Commission % stay blank for staff to confirm; address, parties, price, dates fill from PDF.
 */
export function mergePdfTradeKeyInfo(prev, tradeFields = {}) {
  const SKIP_FROM_PDF = new Set([
    "conditions",
    "listCommission",
    "sellCommission",
    "entryDate",
  ]);
  const today = (() => {
    const d = new Date();
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  })();
  const fromPdf = Object.fromEntries(
    Object.entries(tradeFields).filter(
      ([key, value]) =>
        !SKIP_FROM_PDF.has(key) && value !== "" && value != null
    )
  );
  const merged = {
    ...prev,
    ...fromPdf,
    // Leave commission % blank for staff to confirm
    listCommission: "",
    sellCommission: "",
    // Entry date is always the system date
    entryDate: today,
    conditions: tradeFields.conditions?.length
      ? tradeFields.conditions.filter(
          (c) =>
            String(c?.conditionText || "").trim() &&
            String(c?.dueDate || "").trim() &&
            !/^no conditions available\.?$/i.test(
              String(c?.conditionText || "").trim()
            )
        )
      : prev.conditions,
  };
  return applyLeaseCommissionDefaults(merged, { overwrite: true });
}
